import React, { useState, useEffect, useCallback } from 'react';

const ReusableImageGallery = ({ 
  images = [], 
  config = {},
  onImageClick,
  onImageLoad,
  className = '',
  showOverlay = true,
  showCounter = true,
  showNavigation = true,
  enableClickOutside = true,
  errorComponent,
  emptyComponent
}) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [errorImages, setErrorImages] = useState(new Set());

  // Configurare default
  const defaultConfig = {
    // Grid settings
    minColumnWidth: 250,
    gap: 16,
    thumbnailHeight: 200,
    
    // Responsive breakpoints
    mobileColumns: 1,
    tabletColumns: 2,
    desktopColumns: 'auto-fit',
    
    // Modal sizes
    modalSizes: {
      small: { mobile: '70vw', desktop: '30vw' },
      medium: { mobile: '80vw', desktop: '45vw' },
      large: { mobile: '95vw', desktop: '60vw' },
      default: { mobile: '90vw', desktop: '50vw' }
    },
    
    // Animation
    transitionDuration: 300,
    hoverScale: 1.05,
    
    // Colors
    overlayColor: 'rgba(0, 0, 0, 0.9)',
    overlayTextColor: 'rgba(0, 0, 0, 0.3)',
    
    // Text
    overlayText: 'Mareste imaginea',
    loadingText: 'Se incarca...',
    errorText: 'Eroare la incarcare',
    emptyText: 'Nici o imagina disponibila',
    
    // Features
    lazyLoading: true,
    infiniteScroll: false,
    autoplay: false
  };

  const finalConfig = { ...defaultConfig, ...config };

  
  const sampleImages = [
    {
      id: 1,
      thumbnail: 'https://picsum.photos/300/200?random=1',
      fullSize: 'https://picsum.photos/1200/800?random=1',
      alt: 'Landscape Image',
      modalSize: 'large',
      caption: 'Beautiful landscape view'
    },
    {
      id: 2,
      thumbnail: 'https://picsum.photos/300/200?random=2',
      fullSize: 'https://picsum.photos/600/900?random=2',
      alt: 'Portrait Image',
      modalSize: 'small',
      caption: 'Portrait photography'
    },
    {
      id: 3,
      thumbnail: 'https://picsum.photos/300/200?random=3',
      fullSize: 'https://picsum.photos/800/800?random=3',
      alt: 'Square Image',
      modalSize: 'medium',
      caption: 'Square composition'
    }
  ];

  const galleryImages = images.length > 0 ? images : sampleImages;
  const imagesCount = galleryImages.length;

  const openModal = (index) => {
    setSelectedIndex(index);
    if (onImageClick) {
      onImageClick(galleryImages[index], index);
    }
  };

  const handleImageLoad = (imageId) => {
    if (onImageLoad) {
      const image = galleryImages.find(img => img.id === imageId);
      onImageLoad(image);
    }
  };

  const handleImageError = (imageId) => {
    setErrorImages(prev => new Set([...prev, imageId]));
  };

  const getModalSizeClass = (image) => {
    const size = image.modalSize || 'default';
    return `modal-size-${size}`;
  };

  const getGridColumns = () => {
    const { desktopColumns, minColumnWidth } = finalConfig;
    
    if (desktopColumns === 'auto-fit') {
      return `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`;
    }
    return `repeat(${desktopColumns}, 1fr)`;
  };

  const closeModal = useCallback(() => {
    setSelectedIndex(null);
  }, []);
  
  const goToPrevious = useCallback(() => {
    setSelectedIndex((prev) => 
      prev > 0 ? prev - 1 : imagesCount - 1
    );
  }, [imagesCount]);

  const goToNext = useCallback(() => {
    setSelectedIndex((prev) => 
      prev < imagesCount - 1 ? prev + 1 : 0
    );
  }, [imagesCount]);

  const handleKeyDown = useCallback((e) => {
    switch(e.key) {
      case 'Escape':
        closeModal();
        break;
      case 'ArrowLeft':
        goToPrevious();
        break;
      case 'ArrowRight':
        goToNext();
        break;
      default:
        break;
    }
  }, [closeModal, goToPrevious, goToNext]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Generate dynamic CSS based on config
  const dynamicStyles = `
    .reusable-gallery-container {
      width: 100%;
      max-width: 1150px;
      margin: 0 auto;
      padding: ${finalConfig.gap}px;
    }

    .reusable-gallery-grid {
      display: grid;
      grid-template-columns: ${getGridColumns()};
      gap: ${finalConfig.gap}px;
    }

    .reusable-thumbnail-container {
      position: relative;
      cursor: pointer;
      overflow: hidden;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      transition: all ${finalConfig.transitionDuration}ms ease;
      transform: translateZ(0);
    }

    .reusable-thumbnail-container:hover {
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      transform: translateZ(0) scale(${finalConfig.hoverScale});
    }

    .reusable-thumbnail-image {
      width: 100%;
      height: ${finalConfig.thumbnailHeight}px;
      object-fit: cover;
      display: block;
    }

    .reusable-thumbnail-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: transparent;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      transition: background-color ${finalConfig.transitionDuration}ms ease;
      opacity: 0;
    }

    .reusable-thumbnail-container:hover .reusable-thumbnail-overlay {
      background-color: ${finalConfig.overlayTextColor};
      opacity: 1;
    }

    .reusable-overlay-text {
      color: white;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 4px;
    }

    .reusable-overlay-caption {
      color: white;
      font-size: 12px;
      opacity: 0.8;
      text-align: center;
      padding: 0 8px;
    }

    .reusable-gallery-modal {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: ${finalConfig.overlayColor};
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 16px;
    }

    .reusable-modal-content {
      position: relative;
      max-width: ${finalConfig.modalSizes.default.mobile};
      max-height: ${finalConfig.modalSizes.default.mobile};
      margin: auto;
    }

    .modal-size-small {
      max-width: ${finalConfig.modalSizes.small.mobile} !important;
      max-height: ${finalConfig.modalSizes.small.mobile} !important;
    }

    .modal-size-medium {
      max-width: ${finalConfig.modalSizes.medium.mobile} !important;
      max-height: ${finalConfig.modalSizes.medium.mobile} !important;
    }

    .modal-size-large {
      max-width: ${finalConfig.modalSizes.large.mobile} !important;
      max-height: ${finalConfig.modalSizes.large.mobile} !important;
    }

    .reusable-modal-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 8px;
    }

    .reusable-modal-button {
      position: absolute;
      background-color: rgba(0, 0, 0, 0.5);
      color: white;
      border: none;
      border-radius: 50%;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background-color 0.2s ease;
      font-size: 20px;
    }

    .reusable-modal-button:hover {
      background-color: rgba(0, 0, 0, 0.7);
    }

    .reusable-close-button {
      top: 16px;
      right: 16px;
    }

    .reusable-prev-button {
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
    }

    .reusable-next-button {
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
    }

    .reusable-image-counter {
      position: absolute;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(0, 0, 0, 0.5);
      color: white;
      padding: 8px 16px;
      border-radius: 24px;
      font-size: 14px;
    }

    .reusable-loading, .reusable-error {
      display: flex;
      align-items: center;
      justify-content: center;
      height: ${finalConfig.thumbnailHeight}px;
      background-color: #f0f0f0;
      color: #666;
      font-size: 14px;
    }

    .reusable-empty {
      grid-column: 1 / -1;
      text-align: center;
      padding: 40px;
      color: #666;
    }

    @media (max-width: 768px) {
      .reusable-gallery-grid {
        grid-template-columns: repeat(${finalConfig.tabletColumns}, 1fr);
      }
    }

    @media (max-width: 480px) {
      .reusable-gallery-grid {
        grid-template-columns: repeat(${finalConfig.mobileColumns}, 1fr);
      }
      
      .reusable-modal-button {
        width: 40px;
        height: 40px;
        font-size: 18px;
      }
    }

    @media (min-width: 769px) {
      .reusable-modal-content {
        max-width: ${finalConfig.modalSizes.default.desktop};
        max-height: 85vh;
      }

      .modal-size-small {
        max-width: ${finalConfig.modalSizes.small.desktop} !important;
        max-height: 70vh !important;
      }

      .modal-size-medium {
        max-width: ${finalConfig.modalSizes.medium.desktop} !important;
        max-height: 75vh !important;
      }

      .modal-size-large {
        max-width: ${finalConfig.modalSizes.large.desktop} !important;
        max-height: 85vh !important;
      }
    }
  `;

  if (galleryImages.length === 0) {
    return (
      <div className={`reusable-gallery-container ${className}`}>
        <style>{dynamicStyles}</style>
        {emptyComponent || (
          <div className="reusable-empty">
            {finalConfig.emptyText}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <style>{dynamicStyles}</style>
      
      <div className={`reusable-gallery-container ${className}`}>
        <div className="reusable-gallery-grid">
          {galleryImages.map((image, index) => {
            const hasError = errorImages.has(image.id);

            return (
              <div
                key={image.id}
                className="reusable-thumbnail-container"
                onClick={() => openModal(index)}
              >
                {hasError ? (
                  errorComponent || (
                    <div className="reusable-error">
                      {finalConfig.errorText}
                    </div>
                  )
                ) : (
                  <>
                    <img
                      src={image.thumbnail}
                      alt={image.alt}
                      className="reusable-thumbnail-image"
                      onLoad={() => handleImageLoad(image.id)}
                      onError={() => handleImageError(image.id)}
                      loading={finalConfig.lazyLoading ? "lazy" : "eager"}
                    />
                    
                    {showOverlay && (
                      <div className="reusable-thumbnail-overlay">
                        <div className="reusable-overlay-text">
                          {finalConfig.overlayText}
                        </div>
                        {image.caption && (
                          <div className="reusable-overlay-caption">
                            {image.caption}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Modal */}
        {selectedIndex !== null && (
          <div 
            className="reusable-gallery-modal" 
            onClick={enableClickOutside ? closeModal : undefined}
          >
            <div 
              className={`reusable-modal-content ${getModalSizeClass(galleryImages[selectedIndex])}`} 
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="reusable-modal-button reusable-close-button"
              >
                ✕
              </button>

              {/* Navigation Buttons */}
              {showNavigation && galleryImages.length > 1 && (
                <>
                  <button
                    onClick={goToPrevious}
                    className="reusable-modal-button reusable-prev-button"
                  >
                    ‹
                  </button>

                  <button
                    onClick={goToNext}
                    className="reusable-modal-button reusable-next-button"
                  >
                    ›
                  </button>
                </>
              )}

              {/* Main Image */}
              <img
                src={galleryImages[selectedIndex].fullSize}
                alt={galleryImages[selectedIndex].alt}
                className="reusable-modal-image"
              />

              {/* Image Counter */}
              {showCounter && galleryImages.length > 1 && (
                <div className="reusable-image-counter">
                  {selectedIndex + 1} / {galleryImages.length}
                </div>
              )}

              {/* Caption */}
              {galleryImages[selectedIndex].caption && (
                <div className="reusable-image-counter" style={{bottom: '50px'}}>
                  {galleryImages[selectedIndex].caption}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ReusableImageGallery;